/* eslint-disable react/no-unescaped-entities */
import { container_classname } from "@components/new-ui/constants";
import NewLayout from "@components/new-ui/Layout";
import React from "react";
import { MainContent, PressReleaseSummary } from "./news/gallabox-raises-3.5-million-to-fuel-its-mission";

const content1: PressReleaseSummary[] = [
    {
        description: (
            <p>
                If you manage multiple WhatsApp Business numbers, October 7, 2025 marks a complete change in how Meta handles your messaging limits. The platform is moving from
                individual phone number limits to portfolio-based limits
            </p>
        ),
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">The shift from individual to collective limits</h2>,
        description: (
            <>
                <p>
                    Till now, each phone number in your account has its own messaging ceiling. Number A might be able to send 10,000 messages daily, while Number B is stuck at
                    2,000.
                </p>
                <p className="mt-2">
                    Starting October 7, all your numbers will draw from the same pool. If your highest individual limit is 10,000 messages, that becomes your portfolio limit. Every
                    number you manage can now access this shared capacity.
                </p>
            </>
        ),
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">Why this matters for your business</h2>,
        description: (
            <>
                <p>
                    Currently, businesses often face an awkward situation. Their main customer service number might have worked up to a 100,000-message limit through months of
                    quality messaging.
                </p>
                <p className="mt-2">
                    Meanwhile, a newly added sales number starts at just 1,000 messages. Under the new system, that sales number immediately inherits the portfolio's
                    100,000-message capacity.
                </p>
            </>
        ),
    },
    {
        title: <h3 className="text-xl font-semibold">Faster scaling, but with a catch</h3>,
        description: (
            <>
                <p>
                    Meta is also accelerating how quickly limits can increase – from 24 hours to just 6 hours. But here's what they're not emphasizing: this works both ways. Poor
                    messaging practices can now impact your entire portfolio faster than before.
                </p>
                <p className="mt-2">Let me illustrate with a real scenario. Today, if Number B sends spam and gets flagged, only Number B's limit drops.</p>
                <p className="mt-2 font-bold">After October 7, spam from any number could potentially impact the shared limit for all your numbers.</p>
            </>
        ),
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">How your limits are determined</h2>,
        description: (
            <>
                <p>
                    Your portfolio will inherit the highest messaging limit from among all your existing numbers. Meta hasn't clarified what happens if you have numbers with
                    different quality ratings, but based on their documentation patterns, expect the system to be conservative.
                </p>
            </>
        ),
    },
    {
        title: <h3 className="text-xl font-semibold">What triggers limit increases </h3>,
        description: (
            <>
                <p>The 6-hour evaluation windows will assess:</p>
                <ul className="mb-5">
                    <li>Message quality ratings</li>
                    <li>Customer engagement rates</li>
                    <li>Block and report rates</li>
                    <li>Unique recipients (not just message volume)</li>
                </ul>
            </>
        ),
    },
    {
        title: <h3 className="text-xl font-semibold">New number additions</h3>,
        description: (
            <>
                <p>
                    When you add a phone number to your WhatsApp Business account after October 7, it immediately gains access to your portfolio's current limit. No more waiting
                    weeks to scale up a new number.
                </p>
            </>
        ),
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">Strategic implications for different business models</h2>,
        description: (
            <>
                <p>Here are a few recommendations to help you adapt to the pricing change without breaking your existing systems.</p>
                <ol className="ml-6 list-none counter-reset:section">
                    <li className="counter-increment:section before:content-[counter(section)'.'] before:font-semibold before:text-xl before:mr-2">
                        <h3 className="text-xl font-semibold my-3">1. Multi-brand businesses</h3>
                        <p>
                            If you manage separate WhatsApp numbers for different brands or regions, the shared limit system changes your risk profile. A quality issue with one
                            brand's messaging could throttle communications for all brands in your portfolio.
                        </p>
                        <p>
                            <b>Recommendation:</b> Consider separate WhatsApp Business accounts for brands with significantly different risk profiles or messaging patterns.
                        </p>
                    </li>
                    <li className="counter-increment:section before:content-[counter(section)'.'] before:font-semibold before:text-xl before:mr-2">
                        <h3 className="text-xl font-semibold my-3">2. High-volume support operations</h3>
                        <p>
                            For businesses using multiple support numbers to distribute load, the new system simplifies operations. No more manually balancing message distribution
                            to stay within individual limits.
                        </p>
                        <p>
                            <b>Recommendation:</b> Focus on message quality metrics across all numbers rather than load balancing.
                        </p>
                    </li>
                    <li className="counter-increment:section before:content-[counter(section)'.'] before:font-semibold before:text-xl before:mr-2">
                        <h3 className="text-xl font-semibold my-3">3. Growing businesses</h3>
                        <p>
                            The faster scaling timeline (6 hours instead of 24) means businesses can respond more quickly to viral moments or campaign successes. But it also means
                            you need real-time monitoring or a{" "}
                            <a href="https://gallabox.com/whatsapp-shared-inbox" className="text-blue-700" target="_blank" rel="noreferrer">
                                smarter team inbox
                            </a>{" "}
                            to catch issues before they impact your entire operation.
                        </p>
                    </li>
                </ol>
            </>
        ),
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">How to prepare for the transition</h2>,
        description: (
            <>
                <p>You should consider two key actions to ensure your house is in order for the transition.</p>
                <h3 className="text-xl font-semibold my-3">Audit your current numbers</h3>
                <p>Make sure you document:</p>
                <ul className="gap-3">
                    <li>Current limit for each number</li>
                    <li>Quality rating for each number</li>
                    <li>Average daily message volume per number</li>
                    <li>Types of messages sent from each number</li>
                </ul>
                <p>This baseline helps you understand your starting position and identify potential risks when you switch to a portfolio-wide limit.</p>
                <h3 className="text-xl font-semibold my-3">Revise your escalation procedures</h3>
                <p>
                    With limits potentially changing every 6 hours, your team needs faster response protocols. A quality issue at 9 AM could impact your entire operation by 3 PM.
                </p>
            </>
        ),
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">Best practices for Meta’s October 7 pricing update and beyond</h2>,
        description: (
            <ol>
                <li>
                    <b>Monitor actively:</b> Watch for any unexpected changes in your messaging capacity
                </li>
                <li>
                    <b>Test gradually:</b> Don't immediately max out your new shared limit
                </li>
                <li>
                    <b>Document the transition:</b> Note your new portfolio limit and how it was calculated
                </li>
                <li>
                    <b>Track patterns:</b> Monitor how quickly your limits adjust to messaging patterns
                </li>
                <li>
                    <b>Establish new workflows:</b> Adapt to the 6-hour evaluation cycles
                </li>
                <li>
                    <b>Refine distribution:</b> Optimize how you spread messages across numbers
                </li>
            </ol>
        ),
    },
    {
        title: <h2 className="text-2xl font-semibold pb-2">The bottom line</h2>,
        description: (
            <p>
                For well-run operations, this change offers more flexibility and faster growth. For businesses with inconsistent quality across numbers, it might introduce new
                risks.
                <br />
                <br />
                The 6-hour evaluation window particularly changes the game. Quality issues spread faster, but so do limit increases. Success in this new system requires treating
                your WhatsApp presence as a unified operation rather than a collection of independent numbers.
            </p>
        ),
    },
];

const WhatsappBusinessPricingArticle: React.FC = () => {
    return (
        <NewLayout
            title="New WhatsApp Business messaging limits explained (Portfolio-based)"
            description="WhatsApp Business shifts to portfolio-based messaging limits on October 7, 2025. All numbers share one limit. Learn what changes and how to make use of it."
            canonical="https://gallabox.com/whatsapp-business-pricing-october-2025-update"
        >
            <section className="pb-20" style={{ background: "linear-gradient(265deg, #A1D8FF -7.73%, #396AFF 19.04%, #00238D 61.31%, #000 102.16%)" }}>
                <div className={`${container_classname}  font-bold text-white md:h-96 md:pt-40 pt-16 px-6`}>
                    <p className="text-white text-sm font-normal">UPDATES</p>
                    <h1 style={{ fontFamily: "Degular Variable" }} className="lg:text-7xl md:text-5xl text-4xl">
                        WhatsApp messaging limits are now portfolio-wide: Effective October 7, 2025
                    </h1>
                    <p className="text-white text-sm font-normal mt-3">October 7, 2025</p>
                </div>
            </section>
            <section className="pt-20 pb-20">
                <MainContent aboutData={content1} />
            </section>
        </NewLayout>
    );
};

export default WhatsappBusinessPricingArticle;
